import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  BarChart3, TrendingUp, Copy, FileText, Plus, Loader2, CheckCircle2, AlertTriangle,
  ArrowRight, Shield, Globe, Bell, Send, ExternalLink, Zap, Layout, GitCompareArrows,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════
// COMPETITIVE BENCHMARKS
// ══════════════════════════════════════════════════════════════
export function CompetitiveBenchmarks({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/benchmarks/competitive`, { headers })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="competitive-benchmarks">
      <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Competitive Benchmarks</h2><p className="text-sm text-muted-foreground">How your portfolio compares to the industry.</p></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.your_portfolio.avg_solutions}</div><div className="text-[10px] text-muted-foreground">Your Avg Solutions</div></div>
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Space Grotesk' }}>{data.industry.avg_solutions}</div><div className="text-[10px] text-muted-foreground">Industry Avg</div></div>
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.industry.properties}</div><div className="text-[10px] text-muted-foreground">Properties Benchmarked</div></div>
      </div>
      {data.your_gaps?.length > 0 && (
        <div><h3 className="text-sm font-semibold mb-2">Your Gaps vs Industry Standard</h3>
          <div className="space-y-1">{data.your_gaps.map(g => (
            <div key={g.platform_id} className="flex items-center justify-between p-2.5 rounded-lg border bg-amber-50 border-amber-200">
              <span className="text-sm font-medium">{g.platform_name}</span>
              <Badge className="text-[9px] bg-amber-100 text-amber-700">{g.adoption_pct}% of industry uses this</Badge>
            </div>
          ))}</div>
        </div>
      )}
      <div><h3 className="text-sm font-semibold mb-2">Top Industry Platforms</h3>
        <div className="space-y-1">{data.top_platforms?.slice(0, 10).map(p => (
          <div key={p.platform_id} className="flex items-center gap-3 p-2 rounded-lg border bg-card">
            <span className="text-xs font-medium flex-1">{p.platform_name}</span>
            <div className="w-24 h-1.5 rounded-full bg-muted"><div className="h-full rounded-full bg-blue-500" style={{ width: `${p.adoption_pct}%` }} /></div>
            <span className="text-[10px] font-mono text-muted-foreground w-10 text-right">{p.adoption_pct}%</span>
          </div>
        ))}</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// VENDOR MARKETPLACE / RFP BOARD
// ══════════════════════════════════════════════════════════════
export function VendorMarketplace({ token }) {
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', platform_type_id: '', description: '', property_count: 1, total_rooms: 0, budget_range: '', deadline: '' });
  const [saving, setSaving] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetch = useCallback(() => {
    axios.get(`${BACKEND}/api/marketplace/rfps`, { headers }).then(r => setRfps(r.data.rfps || [])).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);
  useEffect(() => { fetch(); axios.get(`${BACKEND}/api/platform-types`, { headers }).then(r => setPlatforms(r.data || [])).catch(() => {}); }, [fetch, headers]);

  const create = async () => {
    setSaving(true);
    try { await axios.post(`${BACKEND}/api/marketplace/rfps`, form, { headers }); toast.success('RFP posted'); setCreateOpen(false); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="vendor-marketplace">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Vendor Marketplace</h2><p className="text-sm text-muted-foreground">Post RFPs and receive vendor proposals.</p></div>
        <Button onClick={() => setCreateOpen(true)} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Post RFP</Button>
      </div>
      {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : rfps.length === 0 ? (
        <div className="py-12 text-center border-2 border-dashed rounded-xl"><FileText className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" /><p className="text-sm text-muted-foreground">No RFPs yet. Post your first one to start receiving vendor proposals.</p></div>
      ) : (
        <div className="space-y-2">{rfps.map(r => (
          <div key={r.id} className="p-4 rounded-lg border bg-card">
            <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">{r.title}</h3><Badge variant="outline" className="text-[9px]">{r.status}</Badge></div>
            <div className="flex gap-3 mt-1 text-xs text-muted-foreground"><span>{r.platform_name}</span><span>{r.property_count} properties</span>{r.budget_range && <span>{r.budget_range}</span>}<span>{r.responses} responses</span></div>
            {r.description && <p className="text-xs text-muted-foreground mt-2">{r.description}</p>}
          </div>
        ))}</div>
      )}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>Post an RFP</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Looking for a new PMS solution" /></div>
          <div><Label className="text-xs">Platform Category</Label><Select value={form.platform_type_id} onValueChange={v => setForm(p => ({ ...p, platform_type_id: v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs">Properties</Label><Input type="number" value={form.property_count} onChange={e => setForm(p => ({ ...p, property_count: parseInt(e.target.value) || 1 }))} /></div><div><Label className="text-xs">Total Rooms</Label><Input type="number" value={form.total_rooms} onChange={e => setForm(p => ({ ...p, total_rooms: parseInt(e.target.value) || 0 }))} /></div></div>
          <div><Label className="text-xs">Budget Range</Label><Input value={form.budget_range} onChange={e => setForm(p => ({ ...p, budget_range: e.target.value }))} placeholder="$50k-100k/yr" /></div>
          <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Describe your requirements..." /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={create} disabled={saving || !form.title || !form.platform_type_id}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post RFP'}</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TEMPLATE STACK LIBRARY
// ══════════════════════════════════════════════════════════════
export function TemplateLibrary({ token, properties, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyProp, setApplyProp] = useState('');
  const [applyTemplate, setApplyTemplate] = useState(null);
  const [applying, setApplying] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/templates/stacks`, { headers }).then(r => setTemplates(r.data.templates || [])).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);

  const apply = async () => {
    if (!applyTemplate || !applyProp) return;
    setApplying(true);
    try {
      const r = await axios.post(`${BACKEND}/api/templates/apply/${applyTemplate.id}?property_id=${applyProp}`, {}, { headers });
      toast.success(`Applied: ${r.data.created} platforms added (${r.data.already_existed} already existed)`);
      setApplyTemplate(null);
      if (onApplied) onApplied();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setApplying(false); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="template-library">
      <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Stack Templates</h2><p className="text-sm text-muted-foreground">Pre-built tech stack templates. Apply to any property to instantly fill platform assignments.</p></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {templates.map(t => (
          <div key={t.id} className="p-4 rounded-lg border bg-card space-y-2">
            <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">{t.name}</h3><Badge variant="outline" className="text-[9px]">{t.platform_count} platforms</Badge></div>
            <div className="flex flex-wrap gap-1">{t.platform_names?.slice(0, 8).map(n => <Badge key={n} variant="secondary" className="text-[8px]">{n}</Badge>)}{t.platform_count > 8 && <Badge variant="secondary" className="text-[8px]">+{t.platform_count - 8} more</Badge>}</div>
            <Button size="sm" variant="outline" className="w-full text-xs gap-1 mt-2" onClick={() => setApplyTemplate(t)}><Zap className="h-3 w-3" /> Apply to Property</Button>
          </div>
        ))}
      </div>
      <Dialog open={!!applyTemplate} onOpenChange={() => setApplyTemplate(null)}><DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Apply Template</DialogTitle></DialogHeader>
        <p className="text-sm">Applying <strong>{applyTemplate?.name}</strong> ({applyTemplate?.platform_count} platforms) to:</p>
        <Select value={applyProp} onValueChange={setApplyProp}><SelectTrigger><SelectValue placeholder="Select property..." /></SelectTrigger><SelectContent>{(properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
        <DialogFooter><Button variant="ghost" onClick={() => setApplyTemplate(null)}>Cancel</Button><Button onClick={apply} disabled={applying || !applyProp}>{applying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PROPERTY DIFF VIEW
// ══════════════════════════════════════════════════════════════
export function PropertyDiffView({ token, properties }) {
  const [propA, setPropA] = useState('');
  const [propB, setPropB] = useState('');
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const compare = async () => {
    if (!propA || !propB) return;
    setLoading(true);
    try { const r = await axios.get(`${BACKEND}/api/properties/diff?property_a=${propA}&property_b=${propB}`, { headers }); setDiff(r.data); }
    catch { toast.error('Failed to compare'); } finally { setLoading(false); }
  };

  const STATUS_COLORS = { both: 'bg-emerald-50 border-emerald-200', a_only: 'bg-blue-50 border-blue-200', b_only: 'bg-amber-50 border-amber-200' };

  return (
    <div className="space-y-4" data-testid="property-diff">
      <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Property Diff</h2><p className="text-sm text-muted-foreground">Compare tech stacks between two properties.</p></div>
      <div className="flex gap-3 items-end">
        <div className="flex-1"><Label className="text-xs">Property A</Label><Select value={propA} onValueChange={setPropA}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{(properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
        <GitCompareArrows className="h-5 w-5 text-muted-foreground shrink-0 mb-2" />
        <div className="flex-1"><Label className="text-xs">Property B</Label><Select value={propB} onValueChange={setPropB}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{(properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
        <Button onClick={compare} disabled={loading || !propA || !propB} className="gap-1.5 mb-0.5">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitCompareArrows className="h-4 w-4" />} Compare</Button>
      </div>
      {diff && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-xl font-bold text-emerald-600">{diff.summary.both}</div><div className="text-[10px] text-muted-foreground">In Both</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-xl font-bold text-blue-600">{diff.summary.a_only}</div><div className="text-[10px] text-muted-foreground">A Only</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-xl font-bold text-amber-600">{diff.summary.b_only}</div><div className="text-[10px] text-muted-foreground">B Only</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-xl font-bold text-red-600">{diff.summary.different_product}</div><div className="text-[10px] text-muted-foreground">Different Product</div></div>
          </div>
          <div className="space-y-1">{diff.diffs?.map(d => (
            <div key={d.platform_id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${STATUS_COLORS[d.status]}`}>
              <span className="text-xs font-medium flex-1">{d.platform_name}</span>
              <span className="text-[10px] text-muted-foreground w-32 truncate text-right">{d.product_a || '—'}</span>
              <span className="text-[10px]">{d.status === 'both' ? (d.same_product ? '=' : '≠') : d.status === 'a_only' ? '←' : '→'}</span>
              <span className="text-[10px] text-muted-foreground w-32 truncate">{d.product_b || '—'}</span>
            </div>
          ))}</div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ONBOARDING PROGRESS
// ══════════════════════════════════════════════════════════════
export function OnboardingProgress({ token, onNavigate }) {
  const [data, setData] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/onboarding/progress`, { headers }).then(r => setData(r.data)).catch(() => {});
  }, [headers]);

  if (!data || data.completed || data.pct >= 100) return null;

  return (
    <div className="border rounded-xl p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 space-y-3" data-testid="onboarding-progress">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Getting Started</h3>
        <span className="text-xs font-mono text-muted-foreground">{data.completed}/{data.total}</span>
      </div>
      <div className="w-full h-2 rounded-full bg-muted"><div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${data.pct}%` }} /></div>
      <div className="space-y-1.5">
        {data.steps?.map(s => (
          <div key={s.id} className="flex items-center gap-2 text-xs">
            {s.completed ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <div className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30 shrink-0" />}
            <span className={s.completed ? 'line-through text-muted-foreground' : 'font-medium'}>{s.label}</span>
            {!s.completed && s.id === 'add_property' && onNavigate && <Button size="sm" variant="ghost" className="h-5 text-[10px] ml-auto" onClick={() => onNavigate('wizard')}>Go</Button>}
            {!s.completed && s.id === 'assign_platforms' && onNavigate && <Button size="sm" variant="ghost" className="h-5 text-[10px] ml-auto" onClick={() => onNavigate('visualizer')}>Go</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SLACK/TEAMS WEBHOOK SETTINGS
// ══════════════════════════════════════════════════════════════
export function WebhookSettings({ token }) {
  const [config, setConfig] = useState({ url: '', channel_type: 'slack', events: ['system_down', 'renewal_approaching', 'incident'], enabled: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/admin/webhooks`, { headers }).then(r => { if (r.data?.url) setConfig(r.data); }).catch(() => {});
  }, [headers]);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${BACKEND}/api/admin/webhooks`, config, { headers }); toast.success('Webhook saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try { await axios.post(`${BACKEND}/api/admin/webhooks/test`, {}, { headers }); toast.success('Test message sent'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setTesting(false); }
  };

  const EVENT_OPTIONS = [
    { id: 'system_down', label: 'System Down/Degraded' },
    { id: 'renewal_approaching', label: 'Contract Renewal Approaching' },
    { id: 'incident', label: 'New Incident Logged' },
    { id: 'rollout_update', label: 'Rollout Status Change' },
    { id: 'compliance_expiry', label: 'Compliance Expiring' },
  ];

  return (
    <div className="space-y-4" data-testid="webhook-settings">
      <div><h3 className="text-sm font-semibold flex items-center gap-2"><Bell className="h-4 w-4" /> Slack / Teams Notifications</h3><p className="text-xs text-muted-foreground mt-1">Send alerts to your team's chat channel.</p></div>
      <div className="flex gap-2">{['slack', 'teams', 'generic'].map(t => <button key={t} onClick={() => setConfig(p => ({ ...p, channel_type: t }))} className={`px-3 py-1.5 rounded-md text-xs capitalize ${config.channel_type === t ? 'bg-primary text-primary-foreground' : 'bg-muted/60'}`}>{t === 'generic' ? 'Custom URL' : t}</button>)}</div>
      <div><Label className="text-xs">Webhook URL</Label><Input value={config.url} onChange={e => setConfig(p => ({ ...p, url: e.target.value }))} placeholder={config.channel_type === 'slack' ? 'https://hooks.slack.com/services/...' : 'https://...'} className="mt-1 font-mono text-xs" /></div>
      <div><Label className="text-xs mb-2 block">Events</Label>
        <div className="space-y-1.5">{EVENT_OPTIONS.map(ev => (
          <label key={ev.id} className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={config.events?.includes(ev.id)} onChange={e => setConfig(p => ({ ...p, events: e.target.checked ? [...(p.events || []), ev.id] : (p.events || []).filter(x => x !== ev.id) }))} className="rounded" />
            {ev.label}
          </label>
        ))}</div>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving || !config.url} className="gap-1.5">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />} Save</Button>
        <Button variant="outline" onClick={test} disabled={testing || !config.url} className="gap-1.5">{testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Test</Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SOC 2 / SECURITY DASHBOARD
// ══════════════════════════════════════════════════════════════
export function SecurityDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND}/api/platform-security`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="security-dashboard">
      <div><h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Platform Security</h2><p className="text-sm text-muted-foreground">LodgiFlow's security posture and compliance status.</p></div>
      {data.security_features?.map(cat => (
        <div key={cat.category}>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{cat.category}</h3>
          <div className="space-y-1.5">{cat.features.map(f => (
            <div key={f.name} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
              <Shield className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <div><span className="text-sm font-medium">{f.name}</span><p className="text-xs text-muted-foreground mt-0.5">{f.description}</p></div>
              <Badge className="text-[8px] bg-emerald-100 text-emerald-700 shrink-0 ml-auto">{f.status}</Badge>
            </div>
          ))}</div>
        </div>
      ))}
      {data.certifications_in_progress?.length > 0 && (
        <div><h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Certifications In Progress</h3>
          <div className="flex gap-2">{data.certifications_in_progress.map(c => <Badge key={c} variant="outline" className="text-xs">{c}</Badge>)}</div>
        </div>
      )}
    </div>
  );
}
